import { ArrowLeft, Layers, Lock, Pencil, Plus, Trash2 } from 'lucide-react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { AppError } from '@obraplan/core/application/errors/app-error';
import type { PileTypeAverage } from '@obraplan/core/application/use-cases/takeoff/rate-average-use-cases';
import { asId, type PileTypeId, type ProjectId } from '@obraplan/core/domain/shared/ids';
import type { Result } from '@obraplan/core/domain/shared/result';
import { serviceElement } from '@obraplan/core/domain/takeoff/applies-to';
import {
  checkPileTypeFields,
  PILE_TYPE_NAME_MAX_LENGTH,
  pileRateKey,
  pileTypeSteelTotal,
  type PileItemRate,
  type PileRateSnapshot,
  type PileSteel,
  type PileType,
  type PileTypeFields,
} from '@obraplan/core/domain/takeoff/pile';
import type {
  CatalogItem,
  CatalogService,
  ServiceCatalog,
} from '@obraplan/core/domain/takeoff/service-catalog';
import {
  STEEL_GAUGES,
  steelGaugeLabel,
  type SteelGauge,
} from '@obraplan/core/domain/takeoff/steel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { ContainerContext, SessionContext } from '@/features/session/session-context';
import { descricaoDoTipo } from './estacas-da-prancha';
import { metros, paraNumero, paraTexto } from './formato';
import { aprovacaoDe, servicosAprovadosDe, type ServicosAprovados } from './servicos-aprovados';
import {
  acoDaMedia,
  atualizarPelaMedia,
  digitarAco,
  digitarFator,
  fatoresDaMedia,
  mudancasDaMedia,
  trazerAMedia,
  unidadeDoItemNoServico,
  usarAMediaNoAco,
  usarAMediaNoFator,
  type AcrescentarItem,
  type FatorDaMedia,
  type Travas,
} from './trazer-a-media';

/**
 * O cadastro de tipos de estaca da obra (Fase 27, etapa 1b — D1, D16), com a composição de cada
 * tipo (Fase 39, etapa 2 — RN-504 a RN-507).
 *
 * O tipo — "R40", "R60" — guarda o diâmetro, o comprimento padrão e os **kg de aço por
 * bitola**: a proporção entre bitolas muda de um tipo para o outro, e é por isso que o aço da
 * estaca vem do tipo, e não da taxa da disciplina (D12). Corrigir o tipo atualiza toda
 * estaca que não tem comprimento próprio (RN-363) — as estacas apontam para o tipo, não o
 * copiam.
 *
 * Desde a Fase 39 o tipo também guarda a **composição**: o fator de cada item dos serviços que
 * medem a estaca, por diâmetro (D12). Ao cadastrar o tipo, ou mudar o diâmetro, ele recebe a
 * foto da média das obras orçadas, e os serviços de estaca da obra ganham os itens da obra de
 * referência que faltam (RN-505). O fator muda à mão, sem motivo (D9), e a faixa "a média
 * mudou" oferece trocar o que ainda segue a média (RN-507).
 *
 * O diálogo grava a lista inteira de uma vez (`SET_PILE_TYPES`), depois de o domínio conferir
 * cada campo (`checkPileTypeFields`) — a composição também.
 *
 * Diâmetro em **centímetros** e comprimento em **metros**, como o projeto de fundação os
 * escreve ("Ø 40 cm × 16 m"); o tipo guarda os dois em metros.
 */

const SELECT_CLASS =
  'h-11 rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-2 text-sm md:h-9';

/** A busca da média de um diâmetro, em metros (`getPileTypeAverage`). */
type BuscarMedia = (diameter: number) => Promise<Result<PileTypeAverage, AppError>>;

/**
 * A busca da média, quando há com quem falar.
 *
 * Lê os contextos sem exigir o provedor (ao contrário de `useContainer`): a bancada montada sem
 * o app — os testes da prancha, a bancada sem obra — continua cadastrando o tipo como antes, só
 * que sem a média.
 */
function useBuscarMedia(projectId: ProjectId): BuscarMedia | undefined {
  const container = useContext(ContainerContext);
  const session = useContext(SessionContext)?.session;
  return useMemo(
    () =>
      container === undefined || session === undefined
        ? undefined
        : (diameter: number) =>
            container.getPileTypeAverage.execute({ projectId, diameter }, session),
    [container, session, projectId],
  );
}

/**
 * O que o orçamento aprovado segura na composição (Fase 33, RN-509): a mesma leitura da aba de
 * itens (`servicosAprovadosDe`), feita quando o diálogo abre. Sem o app, nada trava — e a trava do
 * desenho (RN-439) continua segurando a quantidade do aprovado na gravação.
 */
function useTravas(
  projectId: ProjectId,
  ativo: boolean,
  catalogo: Pick<ServiceCatalog, 'disciplines'> | undefined,
): Travas {
  const container = useContext(ContainerContext);
  const session = useContext(SessionContext)?.session;
  const [aprovados, setAprovados] = useState<ServicosAprovados | undefined>(undefined);
  useEffect(() => {
    if (!ativo || container === undefined || session === undefined) return;
    let vivo = true;
    container.getProjectApprovedBudget.execute({ projectId }, session).then(
      (lidas) => {
        if (vivo && lidas.ok) setAprovados(servicosAprovadosDe(lidas.value.approvals));
      },
      // Sem a leitura, a tela fica como estava: a trava do desenho segura o que passar.
      () => undefined,
    );
    return () => {
      vivo = false;
    };
  }, [container, session, projectId, ativo]);
  return useMemo(() => travasDe(aprovados, catalogo), [aprovados, catalogo]);
}

function travasDe(
  aprovados: ServicosAprovados | undefined,
  catalogo: Pick<ServiceCatalog, 'disciplines'> | undefined,
): Travas {
  if (aprovados === undefined || aprovados.size === 0) return {};
  const aprovado = (serviceKey: string): boolean =>
    aprovacaoDe(aprovados, serviceKey) !== undefined;
  return {
    servico: aprovado,
    // O kg por bitola é o aço da estaca: com o serviço que o mede aprovado, ele não muda mais.
    aco: (catalogo?.disciplines ?? []).some((disciplina) =>
      disciplina.services.some(
        (servico) =>
          servico.measureBy === 'ACO' &&
          serviceElement(servico) === 'ESTACA' &&
          aprovado(servico.key),
      ),
    ),
  };
}

/** O que está sendo digitado no formulário — texto, porque a vírgula ainda está a caminho. */
interface Formulario {
  /** `undefined` é tipo novo. */
  readonly editando: PileTypeId | undefined;
  readonly nome: string;
  readonly diametroCm: string;
  readonly comprimentoM: string;
  /** Uma entrada por bitola presente; o texto é o kg por estaca. */
  readonly kg: Readonly<Partial<Record<SteelGauge, string>>>;
  /**
   * O aço do tipo como estava: o kg que ninguém mexeu continua com a origem e a foto dele, e o
   * que veio da média aparece marcado "média" (Fase 39).
   */
  readonly acoOriginal: readonly PileSteel[];
}

const FORMULARIO_VAZIO: Formulario = {
  editando: undefined,
  nome: '',
  diametroCm: '',
  comprimentoM: '',
  kg: {},
  acoOriginal: [],
};

function formularioDe(tipo: PileType): Formulario {
  return {
    editando: tipo.id,
    nome: tipo.name,
    diametroCm: paraTexto(Math.round(tipo.diameter * 100 * 1000) / 1000),
    comprimentoM: paraTexto(tipo.length),
    kg: Object.fromEntries(tipo.steel.map((linha) => [linha.gauge, paraTexto(linha.kgPerPile)])),
    acoOriginal: tipo.steel,
  };
}

/** A linha original da bitola, se o kg no campo ainda é o dela. */
function acoIntacto(form: Formulario, gauge: SteelGauge): PileSteel | undefined {
  const original = form.acoOriginal.find((linha) => linha.gauge === gauge);
  const texto = form.kg[gauge];
  if (original === undefined || texto === undefined) return undefined;
  return paraNumero(texto) === original.kgPerPile ? original : undefined;
}

// ── Os textos da origem ─────────────────────────────────────────────────────────

function quantas(n: number, singular: string, plural = `${singular}s`): string {
  return `${String(n)} ${n === 1 ? singular : plural}`;
}

/** `2026-10-23` vira `23/10`. */
function diaEMes(at: string): string {
  return `${at.slice(8, 10)}/${at.slice(5, 7)}`;
}

function quatroCasas(valor: number): string {
  return metros(valor, 4);
}

/** "média · 2 obras · 66 estacas · 23/10" — de onde veio o número (D8). */
function textoDaFoto(foto: PileRateSnapshot): string {
  return `média · ${quantas(foto.projects, 'obra')} · ${quantas(foto.piles, 'estaca')} · ${diaEMes(foto.at)}`;
}

function centimetros(diameter: number): string {
  return paraTexto(Math.round(diameter * 1000) / 10);
}

export function TiposDeEstaca({
  tipos,
  emUso,
  projectId,
  onSalvar,
  catalogo,
  onAcrescentarItens,
  triggerClassName,
}: {
  tipos: readonly PileType[];
  /** Quantas estacas desenhadas ou importadas usam cada tipo — o que impede excluir. */
  emUso: ReadonlyMap<PileTypeId, number>;
  projectId: ProjectId;
  /** A lista inteira dos tipos, depois de conferida. */
  onSalvar: (tipos: readonly PileType[]) => void;
  /**
   * O cadastro de serviços da obra (Fase 39): é nele que o fator do tipo acha o item, e é nele
   * que os itens da referência entram.
   */
  catalogo?: Pick<ServiceCatalog, 'disciplines'> | undefined;
  /** Os itens da obra de referência que faltam nos serviços de estaca (RN-505). */
  onAcrescentarItens?: (acoes: readonly AcrescentarItem[]) => void;
  triggerClassName?: string;
}) {
  const buscar = useBuscarMedia(projectId);
  const [aberto, setAberto] = useState(false);
  const [form, setForm] = useState<Formulario>(FORMULARIO_VAZIO);
  const [erro, setErro] = useState<string | undefined>(undefined);
  const [aviso, setAviso] = useState<string | undefined>(undefined);
  /** O que a média trouxe ao último tipo gravado — e o que ela não pôde trazer. */
  const [retorno, setRetorno] = useState<readonly string[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [composicaoDe, setComposicaoDe] = useState<PileTypeId | undefined>(undefined);
  const travas = useTravas(projectId, aberto, catalogo);

  // A resposta da média chega depois do clique: grava sobre a lista de agora, e não a do clique.
  const tiposAgora = useRef(tipos);
  tiposAgora.current = tipos;

  const bitolasLivres = STEEL_GAUGES.filter((info) => form.kg[info.code] === undefined);
  const emComposicao =
    composicaoDe === undefined ? undefined : tipos.find((tipo) => tipo.id === composicaoDe);

  function limpar(): void {
    setForm(FORMULARIO_VAZIO);
    setErro(undefined);
    setAviso(undefined);
  }

  function gravar(
    campos: PileTypeFields,
    editando: PileTypeId | undefined,
    rates: readonly PileItemRate[] | undefined,
  ): void {
    const agora = new Date().toISOString();
    const lista = tiposAgora.current;
    const composicao = rates === undefined ? {} : { rates };
    if (editando === undefined) {
      onSalvar([
        ...lista,
        {
          id: asId<'PileType'>(crypto.randomUUID()),
          projectId,
          ...campos,
          ...composicao,
          createdAt: agora,
          updatedAt: agora,
        },
      ]);
    } else {
      onSalvar(
        lista.map((tipo) =>
          tipo.id === editando ? { ...tipo, ...campos, ...composicao, updatedAt: agora } : tipo,
        ),
      );
    }
    limpar();
  }

  /**
   * A foto da média do diâmetro, e os itens que faltam (RN-505). Sem a resposta, o tipo não é
   * gravado: gravá-lo sem a média deixaria o diâmetro novo com a composição do antigo (D6).
   */
  async function trazerEGravar(
    campos: PileTypeFields,
    editando: PileTypeId | undefined,
    buscarMedia: BuscarMedia,
  ): Promise<void> {
    setBuscando(true);
    let resposta: Result<PileTypeAverage, AppError> | undefined;
    try {
      resposta = await buscarMedia(campos.diameter);
    } catch {
      resposta = undefined;
    } finally {
      setBuscando(false);
    }
    if (resposta === undefined || !resposta.ok) {
      setErro(
        `Não foi possível buscar a média do Ø ${centimetros(campos.diameter)} cm` +
          (resposta === undefined ? '.' : `: ${resposta.error.message}`) +
          ' O tipo não foi gravado; tente de novo.',
      );
      return;
    }
    const anterior = tiposAgora.current.find((tipo) => tipo.id === editando);
    const trazida = trazerAMedia(
      catalogo,
      resposta.value,
      {
        // O que seguia a média era a do diâmetro de antes: só o digitado fica (D6, D9).
        aco: campos.steel.filter((linha) => linha.origin !== 'MEDIA'),
        fatores: anterior?.rates ?? [],
      },
      travas,
    );
    if (trazida.acoes.length > 0) onAcrescentarItens?.(trazida.acoes);
    gravar({ ...campos, steel: trazida.steel }, editando, trazida.rates);

    const daMedia = trazida.rates.filter((fator) => fator.origin === 'MEDIA').length;
    const nome = campos.name;
    setRetorno([
      ...(daMedia > 0 || trazida.steel.some((linha) => linha.origin === 'MEDIA')
        ? [
            `${nome}: a composição veio da média de ${quantas(resposta.value.projects, 'obra')} e ` +
              `${quantas(resposta.value.pieces, 'estaca')} (${diaEMes(resposta.value.at)}).`,
          ]
        : []),
      ...(trazida.acoes.length > 0
        ? [
            `${quantas(trazida.acoes.length, 'item da referência entrou', 'itens da referência entraram')} ` +
              'nos serviços de estaca da obra.',
          ]
        : []),
      ...trazida.avisos,
    ]);
  }

  function salvar(): void {
    const diametro = paraNumero(form.diametroCm);
    const comprimento = paraNumero(form.comprimentoM);
    const aco: PileSteel[] = [];
    for (const info of STEEL_GAUGES) {
      const texto = form.kg[info.code];
      if (texto === undefined) continue;
      // A bitola acrescentada e deixada em branco não é aço: sai, em vez de recusar o tipo.
      if (texto.trim() === '') continue;
      const kg = paraNumero(texto);
      if (kg === undefined) {
        setErro(`O aço de ${steelGaugeLabel(info.code)} precisa ser um número.`);
        return;
      }
      // O kg que ninguém mexeu fica com a origem e a foto; o mexido vira digitado, com a média
      // ao lado (D9).
      const original = form.acoOriginal.find((linha) => linha.gauge === info.code);
      aco.push(
        original !== undefined && original.kgPerPile === kg
          ? original
          : {
              gauge: info.code,
              kgPerPile: kg,
              origin: 'DIGITADO',
              ...(original?.average === undefined ? {} : { average: original.average }),
            },
      );
    }

    const conferido = checkPileTypeFields(
      {
        name: form.nome,
        // Número que não é número vira NaN: o domínio recusa dizendo que falta a medida.
        diameter: diametro === undefined ? Number.NaN : diametro / 100,
        length: comprimento ?? Number.NaN,
        steel: aco,
      },
      tipos,
      form.editando,
    );
    if (!conferido.ok) {
      setErro(conferido.error.message);
      return;
    }

    // A foto é tirada quando o tipo nasce ou muda de diâmetro (D12, RN-505).
    const anterior = tipos.find((tipo) => tipo.id === form.editando);
    const mudouDeDiametro =
      anterior === undefined || anterior.diameter !== conferido.value.diameter;
    setRetorno([]);
    if (buscar !== undefined && mudouDeDiametro) {
      void trazerEGravar(conferido.value, form.editando, buscar);
      return;
    }
    gravar(conferido.value, form.editando, undefined);
  }

  function excluir(tipo: PileType): void {
    const quantasEstacas = emUso.get(tipo.id) ?? 0;
    if (quantasEstacas > 0) {
      setAviso(
        `O tipo ${tipo.name} tem ${quantas(quantasEstacas, 'estaca')}: ` +
          'troque o tipo delas antes de excluí-lo.',
      );
      return;
    }
    onSalvar(tipos.filter((candidato) => candidato.id !== tipo.id));
    if (form.editando === tipo.id) setForm(FORMULARIO_VAZIO);
    setAviso(undefined);
  }

  /** A composição mudou: grava o tipo inteiro na lista inteira, como o formulário. */
  function mudarTipo(novo: PileType): void {
    const agora = new Date().toISOString();
    onSalvar(
      tiposAgora.current.map((tipo) =>
        tipo.id === novo.id ? { ...novo, updatedAt: agora } : tipo,
      ),
    );
  }

  // O diâmetro novo de um tipo que tinha aço da média: ao salvar, vem a média dele (D6).
  const diametroDoForm = paraNumero(form.diametroCm);
  const original = tipos.find((tipo) => tipo.id === form.editando);
  const diametroNovo =
    buscar !== undefined &&
    original !== undefined &&
    diametroDoForm !== undefined &&
    Math.abs(diametroDoForm / 100 - original.diameter) > 0.0005 &&
    form.acoOriginal.some((linha) => linha.origin === 'MEDIA')
      ? diametroDoForm / 100
      : undefined;

  return (
    <Dialog
      open={aberto}
      onOpenChange={(abrir) => {
        setAberto(abrir);
        if (!abrir) {
          limpar();
          setRetorno([]);
          setComposicaoDe(undefined);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="secondary"
          size="sm"
          {...(triggerClassName ? { className: triggerClassName } : {})}
        >
          Tipos de estaca
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Tipos de estaca</DialogTitle>
          <DialogDescription>
            O tipo dá o diâmetro, o comprimento de perfuração, o aço e a composição de cada estaca.
            Corrigir um tipo muda toda estaca que não tem comprimento próprio.
          </DialogDescription>
        </DialogHeader>

        {emComposicao !== undefined ? (
          <ComposicaoDoTipo
            tipo={emComposicao}
            catalogo={catalogo}
            buscar={buscar}
            travas={travas}
            onMudar={mudarTipo}
            onVoltar={() => setComposicaoDe(undefined)}
          />
        ) : (
          <>
            {tipos.length === 0 ? (
              <p className="text-sm text-(--color-content-muted)">
                Nenhum tipo cadastrado. Comece pelo R40 ou pelo R60 do projeto de fundação.
              </p>
            ) : (
              <ul aria-label="Tipos cadastrados" className="flex flex-col gap-2">
                {tipos.map((tipo) => {
                  const quantasEstacas = emUso.get(tipo.id) ?? 0;
                  return (
                    <li
                      key={tipo.id}
                      className="flex min-w-0 flex-wrap items-center gap-2 rounded-md border border-(--color-border-subtle) px-3 py-2"
                    >
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-sm font-semibold" title={tipo.name}>
                          {tipo.name}
                        </span>
                        <span className="text-xs text-(--color-content-muted)">
                          {descricaoDoTipo(tipo)} · {metros(pileTypeSteelTotal(tipo))} kg de aço ·{' '}
                          {quantasEstacas === 0
                            ? 'sem estacas'
                            : `${String(quantasEstacas)} ${quantasEstacas === 1 ? 'estaca' : 'estacas'}`}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Composição do tipo ${tipo.name}`}
                        onClick={() => {
                          limpar();
                          setRetorno([]);
                          setComposicaoDe(tipo.id);
                        }}
                      >
                        <Layers className="size-4" /> Composição
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Editar o tipo ${tipo.name}`}
                        onClick={() => {
                          setForm(formularioDe(tipo));
                          setErro(undefined);
                          setAviso(undefined);
                        }}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Excluir o tipo ${tipo.name}`}
                        onClick={() => excluir(tipo)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
            {aviso !== undefined && (
              <p
                role="alert"
                className="mt-2 rounded-md border border-(--color-state-danger) px-3 py-2 text-sm"
              >
                {aviso}
              </p>
            )}
            {retorno.length > 0 && (
              <ul
                role="status"
                aria-label="O que a média trouxe"
                className="mt-2 flex flex-col gap-1 rounded-md border border-(--color-border-subtle) bg-(--color-surface-muted) px-3 py-2 text-sm"
              >
                {retorno.map((texto) => (
                  <li key={texto}>{texto}</li>
                ))}
              </ul>
            )}

            <form
              aria-label={
                form.editando === undefined ? 'Novo tipo de estaca' : 'Editar tipo de estaca'
              }
              className="mt-4 flex flex-col gap-3 border-t border-(--color-border-subtle) pt-4"
              onSubmit={(evento) => {
                evento.preventDefault();
                salvar();
              }}
            >
              <h3 className="truncate text-sm font-semibold" title={form.nome}>
                {form.editando === undefined ? 'Novo tipo' : `Editando ${form.nome}`}
              </h3>
              <div className="flex flex-wrap items-end gap-2">
                <Field label="Nome do tipo" htmlFor="tipo-estaca-nome">
                  <Input
                    id="tipo-estaca-nome"
                    value={form.nome}
                    maxLength={PILE_TYPE_NAME_MAX_LENGTH}
                    placeholder="R40"
                    className="w-32"
                    onChange={(evento) => setForm({ ...form, nome: evento.target.value })}
                  />
                </Field>
                <Field label="Diâmetro (cm)" htmlFor="tipo-estaca-diametro">
                  <Input
                    id="tipo-estaca-diametro"
                    inputMode="decimal"
                    value={form.diametroCm}
                    placeholder="40"
                    className="w-24"
                    onChange={(evento) => setForm({ ...form, diametroCm: evento.target.value })}
                  />
                </Field>
                <Field label="Perfuração (m)" htmlFor="tipo-estaca-comprimento">
                  <Input
                    id="tipo-estaca-comprimento"
                    inputMode="decimal"
                    value={form.comprimentoM}
                    placeholder="16"
                    className="w-24"
                    onChange={(evento) => setForm({ ...form, comprimentoM: evento.target.value })}
                  />
                </Field>
              </div>

              <fieldset className="flex flex-col gap-2">
                <legend className="text-xs font-medium text-(--color-content-muted)">
                  Aço por estaca, em kg, por bitola
                </legend>
                <p className="text-xs text-(--color-content-muted)">
                  O aço é o da armação de uma estaca e não sai da perfuração: a ferragem não desce a
                  estaca inteira. Corrigir o comprimento de uma estaca não muda o aço dela.
                  {buscar !== undefined &&
                    ' O tipo novo recebe o aço da média do diâmetro; o kg digitado fica como está.'}
                </p>
                {STEEL_GAUGES.filter((info) => form.kg[info.code] !== undefined).map((info) => {
                  const intacto = acoIntacto(form, info.code);
                  return (
                    <div key={info.code} className="flex flex-wrap items-center gap-2">
                      <label htmlFor={`tipo-estaca-${info.code}`} className="w-32 text-sm">
                        {info.label}
                      </label>
                      <Input
                        id={`tipo-estaca-${info.code}`}
                        aria-label={`Quilos de ${info.label}`}
                        inputMode="decimal"
                        value={form.kg[info.code] ?? ''}
                        className="w-28"
                        onChange={(evento) =>
                          setForm({ ...form, kg: { ...form.kg, [info.code]: evento.target.value } })
                        }
                      />
                      <span className="text-xs text-(--color-content-muted)">kg</span>
                      {intacto?.origin === 'MEDIA' && intacto.average !== undefined && (
                        <Badge tone="info" title={textoDaFoto(intacto.average)}>
                          média
                        </Badge>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Tirar a bitola ${info.label}`}
                        onClick={() => {
                          const { [info.code]: _fora, ...resto } = form.kg;
                          setForm({ ...form, kg: resto });
                        }}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  );
                })}
                {diametroNovo !== undefined && (
                  <p className="text-xs text-(--color-state-warn)">
                    O aço marcado "média" é do diâmetro de antes: ao salvar, vem a média do Ø{' '}
                    {centimetros(diametroNovo)} cm.
                  </p>
                )}
                {bitolasLivres.length > 0 && (
                  <select
                    aria-label="Acrescentar bitola"
                    className={SELECT_CLASS}
                    value=""
                    onChange={(evento) => {
                      const escolhida = evento.target.value as SteelGauge;
                      if (escolhida !== ('' as string)) {
                        setForm({ ...form, kg: { ...form.kg, [escolhida]: '' } });
                      }
                    }}
                  >
                    <option value="">Acrescentar bitola…</option>
                    {bitolasLivres.map((info) => (
                      <option key={info.code} value={info.code}>
                        {info.label}
                      </option>
                    ))}
                  </select>
                )}
              </fieldset>

              {erro !== undefined && (
                <p role="alert" className="text-sm text-(--color-state-danger)">
                  {erro}
                </p>
              )}

              <div className="flex flex-wrap gap-2">
                <Button type="submit" variant="primary" size="sm" disabled={buscando}>
                  {buscando ? (
                    'Buscando a média…'
                  ) : form.editando === undefined ? (
                    <>
                      <Plus className="size-4" /> Cadastrar tipo
                    </>
                  ) : (
                    'Salvar tipo'
                  )}
                </Button>
                {form.editando !== undefined && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setForm(FORMULARIO_VAZIO);
                      setErro(undefined);
                    }}
                  >
                    Cancelar
                  </Button>
                )}
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── A composição do tipo (RN-504 a RN-507) ──────────────────────────────────────

type EstadoDaMedia =
  /** Sem com quem falar: a composição mostra a foto guardada. */
  | { readonly estado: 'sem-busca' }
  | { readonly estado: 'carregando' }
  | { readonly estado: 'pronta'; readonly media: PileTypeAverage }
  | { readonly estado: 'falhou'; readonly mensagem: string };

/** A média de hoje do diâmetro, buscada ao abrir a composição — é com ela que a faixa compara. */
function useMediaDeHoje(buscar: BuscarMedia | undefined, diametro: number): EstadoDaMedia {
  const [estado, setEstado] = useState<EstadoDaMedia>(
    buscar === undefined ? { estado: 'sem-busca' } : { estado: 'carregando' },
  );
  useEffect(() => {
    if (buscar === undefined) {
      setEstado({ estado: 'sem-busca' });
      return;
    }
    let vivo = true;
    setEstado({ estado: 'carregando' });
    buscar(diametro).then(
      (resposta) => {
        if (!vivo) return;
        setEstado(
          resposta.ok
            ? { estado: 'pronta', media: resposta.value }
            : { estado: 'falhou', mensagem: resposta.error.message },
        );
      },
      () => {
        if (vivo) setEstado({ estado: 'falhou', mensagem: 'sem resposta do servidor.' });
      },
    );
    return () => {
      vivo = false;
    };
  }, [buscar, diametro]);
  return estado;
}

/** Um serviço que mede a estaca, com os itens que têm fator (o aço vai por bitola). */
interface ServicoDaComposicao {
  readonly servico: CatalogService;
  readonly itens: readonly CatalogItem[];
}

/**
 * Os serviços que a composição mostra: os que medem a estaca, e os que o tipo ou a média já
 * apontam (o serviço de elemento ainda pendente não some com o fator dentro). O item de aço
 * (com bitola) não tem fator: é o kg por estaca, logo abaixo.
 */
function servicosDaComposicao(
  catalogo: Pick<ServiceCatalog, 'disciplines'> | undefined,
  apontados: ReadonlySet<string>,
): readonly ServicoDaComposicao[] {
  return (catalogo?.disciplines ?? []).flatMap((disciplina) =>
    disciplina.services.flatMap((servico) => {
      if (serviceElement(servico) !== 'ESTACA' && !apontados.has(servico.key)) return [];
      const itens = servico.items.filter((item) => item.steelGauge === undefined);
      return itens.length === 0 ? [] : [{ servico, itens }];
    }),
  );
}

function ComposicaoDoTipo({
  tipo,
  catalogo,
  buscar,
  travas,
  onMudar,
  onVoltar,
}: {
  readonly tipo: PileType;
  readonly catalogo: Pick<ServiceCatalog, 'disciplines'> | undefined;
  readonly buscar: BuscarMedia | undefined;
  /** O serviço aprovado só se lê, e o "Atualizar" o pula (RN-509). */
  readonly travas: Travas;
  readonly onMudar: (tipo: PileType) => void;
  readonly onVoltar: () => void;
}) {
  const hoje = useMediaDeHoje(buscar, tipo.diameter);
  const media = hoje.estado === 'pronta' ? hoje.media : undefined;
  const [erro, setErro] = useState<string | undefined>(undefined);

  const daMedia = useMemo(
    () => (media === undefined ? new Map<string, FatorDaMedia>() : fatoresDaMedia(catalogo, media)),
    [catalogo, media],
  );
  const acoHoje = useMemo(
    () => (media === undefined ? new Map<SteelGauge, PileRateSnapshot>() : acoDaMedia(media)),
    [media],
  );
  const mudancas = useMemo(
    () => (media === undefined ? [] : mudancasDaMedia(tipo, catalogo, media, travas)),
    [tipo, catalogo, media, travas],
  );
  const travado = (serviceKey: string): boolean => travas.servico?.(serviceKey) === true;

  const rates = tipo.rates ?? [];
  const fatorDo = new Map(
    rates.map((linha) => [pileRateKey(linha.serviceKey, linha.itemKey), linha]),
  );
  const apontados = new Set([
    ...rates.map((linha) => linha.serviceKey),
    ...[...daMedia.values()].map((fator) => fator.serviceKey),
  ]);
  const servicos = servicosDaComposicao(catalogo, apontados);
  const bitolas = STEEL_GAUGES.filter(
    (info) => tipo.steel.some((linha) => linha.gauge === info.code) || acoHoje.has(info.code),
  );
  const desde = mudancas
    .map((mudanca) => mudanca.desde)
    .filter((dia): dia is string => dia !== undefined)
    .sort()[0];

  /** O texto do campo virou número? Vazio é "sem fator"; o resto, recusa com o motivo. */
  function lerNumero(
    texto: string,
    rotulo: string,
  ): { ok: true; valor: number | undefined } | { ok: false } {
    if (texto.trim() === '') return { ok: true, valor: undefined };
    const valor = paraNumero(texto);
    if (valor === undefined || valor < 0) {
      setErro(`${rotulo} precisa ser um número maior ou igual a zero.`);
      return { ok: false };
    }
    setErro(undefined);
    return { ok: true, valor };
  }

  function mudarFator(servicoKey: string, item: CatalogItem, texto: string): void {
    const lido = lerNumero(texto, `O fator de ${item.description}`);
    if (!lido.ok) return;
    const chave = pileRateKey(servicoKey, item.key);
    const foto = daMedia.get(chave)?.average;
    const novas = digitarFator(rates, servicoKey, item.key, lido.valor, foto);
    if (novas !== rates) onMudar({ ...tipo, rates: novas });
  }

  function mudarAco(gauge: SteelGauge, texto: string): void {
    const lido = lerNumero(texto, `O aço de ${steelGaugeLabel(gauge)}`);
    if (!lido.ok) return;
    const novo = digitarAco(tipo.steel, gauge, lido.valor, acoHoje.get(gauge));
    if (novo !== tipo.steel) onMudar({ ...tipo, steel: novo });
  }

  return (
    <section aria-label={`Composição do ${tipo.name}`} className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onVoltar}>
          <ArrowLeft className="size-4" /> Tipos
        </Button>
        <h3 className="min-w-0 flex-1 truncate text-sm font-semibold" title={tipo.name}>
          Composição do {tipo.name}{' '}
          <span className="font-normal text-(--color-content-muted)">
            · {descricaoDoTipo(tipo)}
          </span>
        </h3>
      </div>
      <p className="text-xs text-(--color-content-muted)">
        Quanto de cada item vai por unidade da medida do serviço, numa estaca deste tipo. O número
        nasce da média das obras orçadas, com a perda dentro; mudar o fator não pede motivo. Vazio
        deixa o item com o índice da obra.
      </p>

      {mudancas.length > 0 && media !== undefined && (
        <div
          role="status"
          aria-label="A média mudou"
          className="flex flex-col gap-2 rounded-md border border-(--color-state-warn) bg-(--color-surface-muted) p-3 text-sm"
        >
          <p className="font-medium">
            A média mudou{desde === undefined ? '' : ` desde ${diaEMes(desde)}`}
          </p>
          <ul className="flex flex-col gap-0.5 text-xs">
            {mudancas.map((mudanca) => (
              <li key={mudanca.chave} className="tabular">
                {mudanca.descricao}: {quatroCasas(mudanca.de)} → {quatroCasas(mudanca.para)}{' '}
                <span className="text-(--color-content-muted)">{mudanca.unidade}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-(--color-content-muted)">
            Atualizar troca só o que segue a média; o fator digitado fica, com a média nova ao lado.
          </p>
          <div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                const atualizado = atualizarPelaMedia(tipo, catalogo, media, travas);
                onMudar({ ...tipo, rates: atualizado.rates, steel: atualizado.steel });
              }}
            >
              Atualizar pela média
            </Button>
          </div>
        </div>
      )}

      {hoje.estado === 'carregando' && (
        <p className="text-xs text-(--color-content-muted)">Buscando a média de hoje…</p>
      )}
      {hoje.estado === 'falhou' && (
        <p className="text-xs text-(--color-state-warn)">
          Não foi possível buscar a média de hoje: {hoje.mensagem} A composição mostra a foto
          guardada.
        </p>
      )}
      {media !== undefined && media.reference === undefined && (
        <p className="text-xs text-(--color-state-warn)">
          Sem obra de referência, não há média. A referência se escolhe em Configurações → Serviços
          → Taxas médias.
        </p>
      )}
      {media !== undefined && media.reference !== undefined && media.pieces === 0 && (
        <p className="text-xs text-(--color-state-warn)">
          Nenhuma estaca Ø {centimetros(tipo.diameter)} cm nas obras da média: os fatores ficam com
          o que for digitado.
        </p>
      )}
      {erro !== undefined && (
        <p role="alert" className="text-sm text-(--color-state-danger)">
          {erro}
        </p>
      )}

      {servicos.length === 0 ? (
        <p className="text-sm text-(--color-content-muted)">
          A obra ainda não tem serviço que mede a estaca com itens. Cadastre os itens em Itens por
          serviço para dar fator a eles.
        </p>
      ) : (
        servicos.map(({ servico, itens }) => (
          <section key={servico.key} aria-label={servico.name} className="flex flex-col gap-1">
            <h4 className="flex flex-wrap items-center gap-2 border-b border-(--color-border-subtle) pb-1 text-xs font-semibold uppercase tracking-[0.06em]">
              {servico.name}
              {travado(servico.key) && <Aprovado />}
            </h4>
            <ul className="flex flex-col divide-y divide-(--color-border-subtle)">
              {itens.map((item) => {
                const chave = pileRateKey(servico.key, item.key);
                const fator = fatorDo.get(chave);
                const atual = daMedia.get(chave);
                return (
                  <LinhaDaComposicao
                    key={chave}
                    descricao={item.description}
                    {...(item.code === undefined ? {} : { codigo: item.code })}
                    unidade={atual?.unidade ?? unidadeDoItemNoServico(item, servico)}
                    valor={fator?.rate}
                    origem={fator?.origin}
                    foto={fator?.average}
                    mediaDeHoje={atual?.average}
                    casas={6}
                    travado={travado(servico.key)}
                    onDigitar={(texto) => mudarFator(servico.key, item, texto)}
                    onUsarAMedia={(foto) =>
                      onMudar({
                        ...tipo,
                        rates: usarAMediaNoFator(rates, {
                          serviceKey: servico.key,
                          itemKey: item.key,
                          average: foto,
                        }),
                      })
                    }
                  />
                );
              })}
            </ul>
          </section>
        ))
      )}

      <section aria-label="Aço por estaca" className="flex flex-col gap-1">
        <h4 className="flex flex-wrap items-center gap-2 border-b border-(--color-border-subtle) pb-1 text-xs font-semibold uppercase tracking-[0.06em]">
          Aço por estaca
          {travas.aco === true && <Aprovado />}
        </h4>
        {bitolas.length === 0 ? (
          <p className="text-sm text-(--color-content-muted)">
            Sem aço neste tipo. Acrescente a bitola editando o tipo.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-(--color-border-subtle)">
            {bitolas.map((info) => {
              const linha = tipo.steel.find((candidata) => candidata.gauge === info.code);
              return (
                <LinhaDaComposicao
                  key={info.code}
                  descricao={info.label}
                  unidade="KG/estaca"
                  valor={linha?.kgPerPile}
                  // O aço de antes da Fase 39 não tem origem: é o digitado.
                  origem={linha === undefined ? undefined : (linha.origin ?? 'DIGITADO')}
                  foto={linha?.average}
                  mediaDeHoje={acoHoje.get(info.code)}
                  casas={3}
                  travado={travas.aco === true}
                  onDigitar={(texto) => mudarAco(info.code, texto)}
                  onUsarAMedia={(foto) =>
                    onMudar({ ...tipo, steel: usarAMediaNoAco(tipo.steel, info.code, foto) })
                  }
                />
              );
            })}
          </ul>
        )}
      </section>
    </section>
  );
}

/** O cadeado do serviço aprovado, como na aba de itens (Fase 33). */
function Aprovado() {
  return (
    <Badge
      tone="ok"
      icon={<Lock />}
      className="normal-case tracking-normal"
      title="Aprovado no orçamento: a composição deste serviço não muda mais."
    >
      aprovado
    </Badge>
  );
}

/**
 * Uma linha da composição: o item, o fator editável, a unidade e de onde veio o número.
 *
 * No celular a origem desce para debaixo do item, e o fator fica sempre à vista ao lado da
 * unidade — é o número que se veio conferir.
 */
function LinhaDaComposicao({
  descricao,
  codigo,
  unidade,
  valor,
  origem,
  foto,
  mediaDeHoje,
  casas,
  travado,
  onDigitar,
  onUsarAMedia,
}: {
  readonly descricao: string;
  readonly codigo?: string;
  readonly unidade: string;
  readonly valor: number | undefined;
  readonly origem: PileItemRate['origin'] | undefined;
  /** A foto guardada no tipo. */
  readonly foto: PileRateSnapshot | undefined;
  /** A média de hoje, quando a busca respondeu. */
  readonly mediaDeHoje: PileRateSnapshot | undefined;
  readonly casas: number;
  /** O serviço aprovado: o número só se lê (RN-509). */
  readonly travado: boolean;
  readonly onDigitar: (texto: string) => void;
  readonly onUsarAMedia: (foto: PileRateSnapshot) => void;
}) {
  // Ao lado do digitado vai a média de hoje; sem resposta da busca, a da foto (D9).
  const aoLado = mediaDeHoje ?? foto;
  let textoDaOrigem: string;
  if (origem === 'MEDIA' && foto !== undefined) textoDaOrigem = textoDaFoto(foto);
  else if (origem === 'MEDIA') textoDaOrigem = 'média';
  else if (origem === 'DIGITADO') {
    textoDaOrigem =
      aoLado === undefined
        ? 'digitado · sem média'
        : `digitado · média ${quatroCasas(aoLado.rate)}`;
  } else {
    textoDaOrigem =
      aoLado === undefined ? 'sem fator' : `sem fator · média ${quatroCasas(aoLado.rate)}`;
  }
  const podeUsarAMedia = !travado && origem !== 'MEDIA' && aoLado !== undefined;

  return (
    <li className="flex flex-col gap-1 py-2 md:flex-row md:items-center md:gap-3">
      <div className="min-w-0 flex-1">
        <span className="text-sm">{descricao}</span>
        {codigo !== undefined && (
          <span className="ml-2 font-mono text-xs text-(--color-content-muted)">{codigo}</span>
        )}
        <span className="block text-xs text-(--color-content-muted)">
          {textoDaOrigem}
          {podeUsarAMedia && (
            <Button
              variant="link"
              size="sm"
              className="ml-1 h-auto px-0 align-baseline text-xs md:h-auto"
              aria-label={`Usar a média em ${descricao}`}
              onClick={() => onUsarAMedia(aoLado)}
            >
              Usar a média
            </Button>
          )}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <CampoDoFator
          // O número que chega de fora (Usar a média, Atualizar) recomeça o campo.
          key={`${String(valor)}|${origem ?? ''}`}
          rotulo={`Fator de ${descricao}`}
          valor={valor}
          casas={casas}
          travado={travado}
          onDigitar={onDigitar}
        />
        <span className="tabular w-24 whitespace-nowrap text-xs text-(--color-content-muted)">
          {unidade}
        </span>
      </div>
    </li>
  );
}

/** O campo do fator: grava ao sair do campo ou no Enter, e não a cada tecla. */
function CampoDoFator({
  rotulo,
  valor,
  casas,
  travado,
  onDigitar,
}: {
  readonly rotulo: string;
  readonly valor: number | undefined;
  readonly casas: number;
  readonly travado: boolean;
  readonly onDigitar: (texto: string) => void;
}) {
  const inicial =
    valor === undefined ? '' : paraTexto(Math.round(valor * 10 ** casas) / 10 ** casas);
  const [texto, setTexto] = useState(inicial);
  return (
    <Input
      aria-label={rotulo}
      inputMode="decimal"
      value={texto}
      placeholder="—"
      className="tabular w-28 text-right"
      disabled={travado}
      onChange={(evento) => setTexto(evento.target.value)}
      onBlur={() => {
        if (texto !== inicial) onDigitar(texto);
      }}
      onKeyDown={(evento) => {
        if (evento.key === 'Enter') {
          evento.preventDefault();
          if (texto !== inicial) onDigitar(texto);
        }
      }}
    />
  );
}
